// src/context/gmc.js

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './auth';
import { usePermissions } from './permissions';
import { extractDateFromBigQueryTimestamp } from '../utils/gmc/merchantHelpers';
import { cacheData, getCachedData, clearCachedData } from '../utils/gmc/cacheData';
import { 
    fetchUsersFromApi,
    fetchAuditLogsFromApi,
    fetchSubAccountAuditLogsFromApi,
    fetchAccountsFromApi,
    fetchPendingRequestsFromApi,
    fetchApprovedRequestsFromApi,
    fetchRejectedRequestsFromApi,
    fetchSubAccountsFromApi,
    fetchSubAccountUsersFromApi,
    createAccessBindingFromApi,
    removeAccessBindingFromApi,
    modifyAccessBindingFromApi,
    approvePendingRequestFromApi,
    rejectPendingRequestFromApi
} from '../utils/gmc/merchantApi';

// Cache keys
const CACHE_KEYS = {
    USERS: 'gmc_users',
    AUDIT_LOGS: 'gmc_audit_logs',
    SUB_ACCOUNT_AUDIT_LOGS: 'gmc_sub_account_audit_logs',
    ACCOUNTS: 'gmc_accounts',
    SUB_ACCOUNTS: 'gmc_sub_accounts',
    SUB_ACCOUNT_USERS: 'gmc_sub_account_users',
    PENDING_REQUESTS: 'gmc_pending_requests',
    APPROVED_REQUESTS: 'gmc_approved_requests',
    REJECTED_REQUESTS: 'gmc_rejected_requests'
};

// Create context
const GmcContext = createContext();

// Context provider component
export const GmcProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const hasGmcPermission = hasPermission('admin/gmc');

    // State for users in BigQuery
    const [users, setUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [usersError, setUsersError] = useState(null);

    // State for audit logs
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLogsLoaded, setAuditLogsLoaded] = useState(false);
    const [auditLogsError, setAuditLogsError] = useState(null);

    // State for sub-account audit logs
    const [subAccountAuditLogs, setSubAccountAuditLogs] = useState([]);
    const [subAccountAuditLogsLoaded, setSubAccountAuditLogsLoaded] = useState(false);
    const [subAccountAuditLogsError, setSubAccountAuditLogsError] = useState(null);

    // State for GMC accounts
    const [accounts, setAccounts] = useState([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // State for GMC sub-accounts
    const [subAccounts, setSubAccounts] = useState([]);
    const [subAccountsLoaded, setSubAccountsLoaded] = useState(false);
    const [subAccountsError, setSubAccountsError] = useState(null);

    // State for GMC sub-account users
    const [subAccountUsers, setSubAccountUsers] = useState([]);
    const [subAccountUsersLoaded, setSubAccountUsersLoaded] = useState(false);
    const [subAccountUsersError, setSubAccountUsersError] = useState(null);

    // State for GMC pending requests
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingRequestsLoaded, setPendingRequestsLoaded] = useState(false);
    const [pendingRequestsError, setPendingRequestsError] = useState(null);

    // State for GMC approved requests
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [approvedRequestsLoaded, setApprovedRequestsLoaded] = useState(false);
    const [approvedRequestsError, setApprovedRequestsError] = useState(null);

    // State for GMC rejected requests
    const [rejectedRequests, setRejectedRequests] = useState([]);
    const [rejectedRequestsLoaded, setRejectedRequestsLoaded] = useState(false);
    const [rejectedRequestsError, setRejectedRequestsError] = useState(null);

    // State to track context initialization
    const [initialized, setInitialized] = useState(false);

    // Refs to prevent duplicate API calls
    const isLoadingUsers = useRef(false);
    const isLoadingAuditLogs = useRef(false);
    const isLoadingSubAccountAuditLogs = useRef(false);
    const isLoadingAccounts = useRef(false);
    const isLoadingSubAccounts = useRef(false);
    const isLoadingSubAccountUsers = useRef(false);
    const isLoadingPendingRequests = useRef(false);
    const isLoadingApprovedRequests = useRef(false);
    const isLoadingRejectedRequests = useRef(false);
    
    // Track if initial data loading has happened
    const hasLoadedInitialData = useRef(false);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching users from BigQuery using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the users
     * @returns {Promise<Array>} Promise resolving to an array of users
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchUsers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingUsers.current) {
            return users;
        }

        // Check if we already have the data and can return it
        if (usersLoaded && users !== null && users.length > 0 && !forceRefresh) {
            return users;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
            if (cachedUsers && cachedUsers.length > 0) {
                setUsers(cachedUsers);
                setUsersLoaded(true);
                setUsersError(null);
                return cachedUsers;
            }
        }
        isLoadingUsers.current = true;
        
        const startTime = performance.now();
        try {
            const userData = await fetchUsersFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${userData.length} users in ${duration.toFixed(2)}ms`);

            // Update state
            setUsers(userData);
            setUsersLoaded(true);
            setUsersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.USERS, userData);

            return userData;
        } catch (error) {
            if (error.response) {
                console.error(`[GMC Context]: Error fetching users (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching users - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching users:', error.message);
            }

            setUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch users data');
            return [];
        } finally {
            isLoadingUsers.current = false;
        }
    }, [users, usersLoaded]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching audit logs using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the audit logs
     * @returns {Promise<Array>} Promise resolving to an array of audit logs
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAuditLogs = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAuditLogs.current) {
            return auditLogs;
        }

        // Check if we already have the data and can return it
        if (auditLogsLoaded && auditLogs !== null && !forceRefresh) {
            return auditLogs;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
            if (cachedLogs && cachedLogs.length > 0) {
                setAuditLogs(cachedLogs);
                setAuditLogsLoaded(true);
                setAuditLogsError(null);
                return cachedLogs;
            }
        }
        isLoadingAuditLogs.current = true;

        const startTime = performance.now();
        try {
            const logsData = await fetchAuditLogsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${logsData.length} audit logs in ${duration.toFixed(2)}ms`);

            // Update state
            setAuditLogs(logsData);
            setAuditLogsLoaded(true);
            setAuditLogsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.AUDIT_LOGS, logsData);

            return logsData;
        } catch (error) {
            if (error.response) {
                console.error(`[GMC Context]: Error fetching audit logs (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching audit logs - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching audit logs:', error.message);
            }

            setAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch audit logs');
            return [];
        } finally {
            isLoadingAuditLogs.current = false;
        }
    }, [auditLogs, auditLogsLoaded]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching sub-account audit logs using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the sub-account audit logs
     * @returns {Promise<Array>} Promise resolving to an array of sub-account audit logs
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchSubAccountAuditLogs = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingSubAccountAuditLogs.current) {
            return subAccountAuditLogs;
        }

        // Check if we already have the data and can return it
        if (subAccountAuditLogsLoaded && subAccountAuditLogs !== null && !forceRefresh) {
            return subAccountAuditLogs;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedLogs = await getCachedData(CACHE_KEYS.SUB_ACCOUNT_AUDIT_LOGS);
            if (cachedLogs && cachedLogs.length > 0) {
                setSubAccountAuditLogs(cachedLogs);
                setSubAccountAuditLogsLoaded(true);
                setSubAccountAuditLogsError(null);
                return cachedLogs;
            }
        }
        isLoadingSubAccountAuditLogs.current = true;

        const startTime = performance.now();
        try {
            const logsData = await fetchSubAccountAuditLogsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${logsData.length} sub-account audit logs in ${duration.toFixed(2)}ms`);

            // Update state
            setSubAccountAuditLogs(logsData);
            setSubAccountAuditLogsLoaded(true);
            setSubAccountAuditLogsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.SUB_ACCOUNT_AUDIT_LOGS, logsData);

            return logsData;
        } catch (error) {
            if (error.response) {
                console.error(`[GMC Context]: Error fetching sub-account audit logs (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching sub-account audit logs - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching sub-account audit logs:', error.message);
            }

            setSubAccountAuditLogsError(error.response?.data?.error?.text || error.message || 'Failed to fetch sub-account audit logs');
            return [];
        } finally {
            isLoadingSubAccountAuditLogs.current = false;
        }
    }, [subAccountAuditLogs, subAccountAuditLogsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GMC accounts using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GMC accounts
     * @returns {Promise<Array>} Promise resolving to an array of GMC accounts
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchAccounts = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingAccounts.current) {
            return accounts;
        }

        // Check if we already have the data and can return it
        if (accountsLoaded && accounts !== null && !forceRefresh) {
            return accounts;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
            if (cachedAccounts && cachedAccounts.length > 0) {
                setAccounts(cachedAccounts);
                setAccountsLoaded(true);
                setAccountsError(null);
                return cachedAccounts;
            }
        }

        // Set loading flag
        isLoadingAccounts.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const accountsData = await fetchAccountsFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${accountsData.length} accounts in ${duration.toFixed(2)}ms`);

            // Update state
            setAccounts(accountsData);
            setAccountsLoaded(true);
            setAccountsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.ACCOUNTS, accountsData);

            return accountsData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GMC Context]: Error fetching accounts (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching accounts - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching accounts:', error.message);
            }

            setAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch accounts');
            return [];
        } finally {
            isLoadingAccounts.current = false;
        }
    }, [accounts, accountsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GMC sub-accounts using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GMC sub-accounts
     * @returns {Promise<Array>} Promise resolving to an array of GMC sub-accounts
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchSubAccounts = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingSubAccounts.current) {
            return subAccounts;
        }

        // Check if we already have the data and can return it
        if (subAccountsLoaded && subAccounts !== null && !forceRefresh) {
            return subAccounts;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedSubAccounts = await getCachedData(CACHE_KEYS.SUB_ACCOUNTS);
            if (cachedSubAccounts && cachedSubAccounts.length > 0) {
                setSubAccounts(cachedSubAccounts);
                setSubAccountsLoaded(true);
                setSubAccountsError(null);
                return cachedSubAccounts;
            }
        }

        // Set loading flag
        isLoadingSubAccounts.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const subAccountsData = await fetchSubAccountsFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${subAccountsData.length} sub-accounts in ${duration.toFixed(2)}ms`);

            // Update state
            setSubAccounts(subAccountsData);
            setSubAccountsLoaded(true);
            setSubAccountsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.SUB_ACCOUNTS, subAccountsData);

            return subAccountsData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GMC Context]: Error fetching sub-accounts (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching sub-accounts - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching sub-accounts:', error.message);
            }

            setSubAccountsError(error.response?.data?.error?.text || error.message || 'Failed to fetch sub-accounts');
            return [];
        } finally {
            isLoadingSubAccounts.current = false;
        }
    }, [subAccounts, subAccountsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching GMC sub-account users using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the GMC sub-account users
     * @returns {Promise<Array>} Promise resolving to an array of GMC sub-account users
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchSubAccountUsers = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingSubAccountUsers.current) {
            return subAccountUsers;
        }

        // Check if we already have the data and can return it
        if (subAccountUsersLoaded && subAccountUsers !== null && subAccountUsers.length > 0 && !forceRefresh) {
            return subAccountUsers;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedSubAccountUsers = await getCachedData(CACHE_KEYS.SUB_ACCOUNT_USERS);
            if (cachedSubAccountUsers && cachedSubAccountUsers.length > 0) {
                setSubAccountUsers(cachedSubAccountUsers);
                setSubAccountUsersLoaded(true);
                setSubAccountUsersError(null);
                return cachedSubAccountUsers;
            }
        }

        // Set loading flag
        isLoadingSubAccountUsers.current = true;

        // Track performance
        const startTime = performance.now();

        try {
            const subAccountUsersData = await fetchSubAccountUsersFromApi(forceRefresh);

            // Log performance
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${subAccountUsersData.length} sub-account users in ${duration.toFixed(2)}ms`);

            // Update state
            setSubAccountUsers(subAccountUsersData);
            setSubAccountUsersLoaded(true);
            setSubAccountUsersError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.SUB_ACCOUNT_USERS, subAccountUsersData);

            return subAccountUsersData;
        } catch (error) {
            // Improved error logging with status code if available
            if (error.response) {
                console.error(`[GMC Context]: Error fetching sub-account users (${error.response.status}):`, 
                    error.response.data?.error?.text || error.response.data?.message || error.message);
            } else if (error.request) {
                console.error('[GMC Context]: Error fetching sub-account users - No response received:', error.message);
            } else {
                console.error('[GMC Context]: Error fetching sub-account users:', error.message);
            }

            setSubAccountUsersError(error.response?.data?.error?.text || error.message || 'Failed to fetch sub-account users');
            return [];
        } finally {
            isLoadingSubAccountUsers.current = false;
        }
    }, [subAccountUsers, subAccountUsersLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching pending requests using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the pending requests
     * @returns {Promise<Array>} Promise resolving to an array of pending requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchPendingRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingPendingRequests.current) {
            return pendingRequests;
        }

        // Check if we already have the data and can return it
        if (pendingRequestsLoaded && pendingRequests !== null && !forceRefresh) {
            return pendingRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setPendingRequests(cachedRequests);
                setPendingRequestsLoaded(true);
                setPendingRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingPendingRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchPendingRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${requestsData.length} pending requests in ${duration.toFixed(2)}ms`);

            // Update state
            setPendingRequests(requestsData);
            setPendingRequestsLoaded(true);
            setPendingRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.PENDING_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GMC Context]: Error fetching pending requests:', error);
            setPendingRequestsError(error.message || 'Failed to fetch pending requests');
            return [];
        } finally {
            isLoadingPendingRequests.current = false;
        }
    }, [pendingRequests, pendingRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching approved requests using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the approved requests
     * @returns {Promise<Array>} Promise resolving to an array of approved requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchApprovedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingApprovedRequests.current) {
            return approvedRequests;
        }

        // Check if we already have the data and can return it
        if (approvedRequestsLoaded && approvedRequests !== null && !forceRefresh) {
            return approvedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setApprovedRequests(cachedRequests);
                setApprovedRequestsLoaded(true);
                setApprovedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingApprovedRequests.current = true;

        const startTime = performance.now();
        try {
            const requestsData = await fetchApprovedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${requestsData.length} approved requests in ${duration.toFixed(2)}ms`);

            // Update state
            setApprovedRequests(requestsData);
            setApprovedRequestsLoaded(true);
            setApprovedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.APPROVED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GMC Context]: Error fetching approved requests:', error);
            setApprovedRequestsError(error.message || 'Failed to fetch approved requests');
            return [];
        } finally {
            isLoadingApprovedRequests.current = false;
        }
    }, [approvedRequests, approvedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for fetching rejected requests using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of the rejected requests
     * @returns {Promise<Array>} Promise resolving to an array of rejected requests
     */
    /* -------------------------------------------------------------------------------------------- */
    const fetchRejectedRequests = useCallback(async (forceRefresh = false) => {
        // Prevent concurrent requests
        if (isLoadingRejectedRequests.current) {
            return rejectedRequests;
        }

        // Check if we already have the data and can return it
        if (rejectedRequestsLoaded && rejectedRequests !== null && !forceRefresh) {
            return rejectedRequests;
        }

        // Check cache if not forcing refresh
        if (!forceRefresh) {
            const cachedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);
            if (cachedRequests && cachedRequests.length >= 0) { // Accept empty arrays
                setRejectedRequests(cachedRequests);
                setRejectedRequestsLoaded(true);
                setRejectedRequestsError(null);
                return cachedRequests;
            }
        }
        isLoadingRejectedRequests.current = true;
        
        const startTime = performance.now();
        try {
            const requestsData = await fetchRejectedRequestsFromApi(forceRefresh);
            const duration = performance.now() - startTime;
            console.log(`[GMC Context]: Fetched ${requestsData.length} rejected requests in ${duration.toFixed(2)}ms`);

            // Update state
            setRejectedRequests(requestsData);
            setRejectedRequestsLoaded(true);
            setRejectedRequestsError(null);

            // Cache the data
            await cacheData(CACHE_KEYS.REJECTED_REQUESTS, requestsData);

            return requestsData;
        } catch (error) {
            console.error('[GMC Context]: Error fetching rejected requests:', error);
            setRejectedRequestsError(error.message || 'Failed to fetch rejected requests');
            return [];
        } finally {
            isLoadingRejectedRequests.current = false;
        }
    }, [rejectedRequests, rejectedRequestsLoaded]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper for refreshing all data at once using the utils GMC API service
     * @param {boolean} forceRefresh - Whether to force a refresh of all data
     * @returns {Promise<boolean>} Promise resolving to a boolean indicating if the refresh was successful
     */
    /* -------------------------------------------------------------------------------------------- */
    const refreshAll = useCallback(async (forceRefresh = true) => {
        // Track performance
        const startTime = performance.now();

        // Run all data refreshes in parallel for better performance
        const promises = [
            fetchUsers(forceRefresh),
            fetchAuditLogs(forceRefresh),
            fetchSubAccountAuditLogs(forceRefresh),
            fetchAccounts(forceRefresh),
            fetchSubAccounts(forceRefresh),
            fetchSubAccountUsers(forceRefresh),
            fetchPendingRequests(forceRefresh),
            fetchApprovedRequests(forceRefresh),
            fetchRejectedRequests(forceRefresh)
        ];

        // Wait for all to complete
        const results = await Promise.all(promises);

        const duration = performance.now() - startTime;
        console.log(`[GMC Context]: Refreshed all GMC data in ${duration.toFixed(2)}ms`);

        return true;
    }, [fetchUsers, fetchAuditLogs, fetchSubAccountAuditLogs, fetchAccounts, fetchSubAccounts, fetchSubAccountUsers, fetchPendingRequests, fetchApprovedRequests, fetchRejectedRequests]);

    
    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & useEffect for eager loading data when user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        // Skip if no user or already loaded
        if (!isAuthenticated || !user?.email || hasLoadedInitialData.current) {
            return;
        }

        // Wait for permissions to load, then check if user has gmc admin access
        if (permissionsLoading) {
            console.log('[GMC Context]: Waiting for permissions to load...');
            return;
        }

        // If user doesn't have gmc admin permission, skip all data fetching
        if (!hasGmcPermission) {
            console.log('[GMC Context]: User lacks admin/gmc permission, skipping data fetch');

            // Set flag to prevent re-triggering
            hasLoadedInitialData.current = true;

            // Mark all data as "loaded" with empty values to prevent loading spinners
            setUsersLoaded(true);
            setAuditLogsLoaded(true);
            setSubAccountAuditLogsLoaded(true);
            setAccountsLoaded(true);
            setSubAccountsLoaded(true);
            setSubAccountUsersLoaded(true);
            setPendingRequestsLoaded(true);
            setApprovedRequestsLoaded(true);
            setRejectedRequestsLoaded(true);

            // Exit early so no API calls will be made
            return;
        }

        const loadInitialData = async () => {
            try {
                // Set flag to prevent multiple loads
                hasLoadedInitialData.current = true;

                // Track performance
                const startTime = performance.now();

                // Check if we have any cached data first
                const cachedUsers = await getCachedData(CACHE_KEYS.USERS);
                const cachedAuditLogs = await getCachedData(CACHE_KEYS.AUDIT_LOGS);
                const cachedSubAccountAuditLogs = await getCachedData(CACHE_KEYS.SUB_ACCOUNT_AUDIT_LOGS);
                const cachedAccounts = await getCachedData(CACHE_KEYS.ACCOUNTS);
                const cachedPendingRequests = await getCachedData(CACHE_KEYS.PENDING_REQUESTS);
                const cachedApprovedRequests = await getCachedData(CACHE_KEYS.APPROVED_REQUESTS);
                const cachedRejectedRequests = await getCachedData(CACHE_KEYS.REJECTED_REQUESTS);

                // Set any cached data we find to prevent flash of loading state
                if (cachedUsers?.length > 0) {
                    setUsers(cachedUsers);
                    setUsersLoaded(true);
                }

                if (cachedAuditLogs?.length > 0) {
                    setAuditLogs(cachedAuditLogs);
                    setAuditLogsLoaded(true);
                }

                if (cachedSubAccountAuditLogs?.length > 0) {
                    setSubAccountAuditLogs(cachedSubAccountAuditLogs);
                    setSubAccountAuditLogsLoaded(true);
                }

                if (cachedAccounts?.length > 0) {
                    setAccounts(cachedAccounts);
                    setAccountsLoaded(true);
                }

                if (cachedPendingRequests?.length !== undefined) { // Accept empty arrays
                    setPendingRequests(cachedPendingRequests || []);
                    setPendingRequestsLoaded(true);
                }

                if (cachedApprovedRequests?.length !== undefined) { // Accept empty arrays
                    setApprovedRequests(cachedApprovedRequests || []);
                    setApprovedRequestsLoaded(true);
                }

                if (cachedRejectedRequests?.length !== undefined) { // Accept empty arrays
                    setRejectedRequests(cachedRejectedRequests || []);
                    setRejectedRequestsLoaded(true);
                }

                // Always fetch fresh data in the background, regardless of cache
                // Create individual timeouts for each data type to prevent blocking on slow endpoints
                const loadWithTimeout = async (fetchFn, dataType, timeoutMs = 90000) => {
                    try {
                        // Create a timeout promise that won't cause unhandled promise rejections
                        const timeoutPromise = new Promise((_, reject) => {
                            const timeoutId = setTimeout(() => {
                                clearTimeout(timeoutId);
                                console.warn(`[GMC Context]: ${dataType} data loading approaching timeout (${timeoutMs/1000}s) - will continue in background`);
                                reject({ isTimeout: true, message: `Timeout reached for ${dataType}` });
                            }, timeoutMs);
                        });

                        try {
                            // Race the actual fetch against the timeout
                            const result = await Promise.race([fetchFn(true), timeoutPromise]);
                            return result;
                        } catch (err) {
                            // Suppress timeout errors to prevent console errors
                            if (err?.isTimeout) {
                                return null;
                            }
                            // Pass through other errors
                            throw err;
                        }
                    } catch (err) {
                        console.error(`[GMC Context]: Error loading ${dataType} data:`, err);
                        return null;
                    }
                };

                // Load all data types in parallel
                const promises = [
                    loadWithTimeout(fetchUsers, 'users'),
                    loadWithTimeout(fetchAuditLogs, 'auditLogs'),
                    loadWithTimeout(fetchSubAccountAuditLogs, 'subAccountAuditLogs'),
                    loadWithTimeout(fetchAccounts, 'accounts'),
                    loadWithTimeout(fetchPendingRequests, 'pendingRequests'),
                    loadWithTimeout(fetchApprovedRequests, 'approvedRequests'),
                    loadWithTimeout(fetchRejectedRequests, 'rejectedRequests')
                ];

                // Wait for all data types to either load or timeout
                const results = await Promise.allSettled(promises);

                // For pending requests, even if the array is empty, consider it loaded
                // Index 4 is now pendingRequests (was 3 before adding subAccountAuditLogs)
                if (results[4].status === 'fulfilled' && results[4].value !== null) {
                    setPendingRequests(results[4].value || []);
                    setPendingRequestsLoaded(true);
                }

                // For approved requests, even if the array is empty, consider it loaded
                // Index 5 is now approvedRequests (was 4 before adding subAccountAuditLogs)
                if (results[5].status === 'fulfilled' && results[5].value !== null) {
                    setApprovedRequests(results[5].value || []);
                    setApprovedRequestsLoaded(true);
                }

                // For rejected requests, even if the array is empty, consider it loaded
                // Index 6 is now rejectedRequests (was 5 before adding subAccountAuditLogs)
                if (results[6].status === 'fulfilled' && results[6].value !== null) {
                    setRejectedRequests(results[6].value || []);
                    setRejectedRequestsLoaded(true);
                }

                // Log any failed requests
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        const dataType = ['users', 'auditLogs', 'subAccountAuditLogs', 'accounts', 'pendingRequests', 'approvedRequests', 'rejectedRequests'][index];
                        console.error(`[GMC Context]: Failed to fetch ${dataType} data:`, result.reason);
                    }
                });

                // Log performance
                const duration = performance.now() - startTime;
                console.log(`[GMC Context]: Initial data loading complete in ${duration.toFixed(2)}ms`);
            } catch (err) {
                console.error('[GMC Context]: Error during initial data loading:', err);
            }
        };

        // Start loading data
        loadInitialData();
    }, [isAuthenticated, user?.email, permissionsLoading, hasGmcPermission]);


    /* -------------------------------------------------------------------------------------------- */
    /**
     * Context wrapper & separate useEffect for marking context as initialized 
     * when auth state is available. This allows components to know when they can 
     * safely use the context.
     * @param {boolean} initialized - Whether the context is initialized
     * @param {boolean} isAuthenticated - Whether the user is authenticated
     * @returns {void}
     */
    /* -------------------------------------------------------------------------------------------- */
    useEffect(() => {
        if (!initialized && isAuthenticated !== null) {
            console.log('[GMC Context]: Context initialized with auth state:', isAuthenticated ? 'authenticated' : 'not authenticated');
            setInitialized(true);
        }
    }, [isAuthenticated, initialized]);

    // Prepare context value
    const contextValue = {
        // User data
        users,
        usersLoaded,
        usersError,

        // Audit logs
        auditLogs,
        auditLogsLoaded,
        auditLogsError,

        // Sub-account audit logs
        subAccountAuditLogs,
        subAccountAuditLogsLoaded,
        subAccountAuditLogsError,

        // GMC accounts
        accounts,
        accountsLoaded,
        accountsError,

        // GMC sub-accounts
        subAccounts,
        subAccountsLoaded,
        subAccountsError,

        // GMC sub-account users
        subAccountUsers,
        subAccountUsersLoaded,
        subAccountUsersError,

        // Pending requests
        pendingRequests,
        pendingRequestsLoaded,
        pendingRequestsError,

        // Approved requests
        approvedRequests,
        approvedRequestsLoaded,
        approvedRequestsError,

        // Rejected requests
        rejectedRequests,
        rejectedRequestsLoaded,
        rejectedRequestsError,

        // Actions
        fetchUsers,
        fetchAuditLogs,
        fetchSubAccountAuditLogs,
        fetchAccounts,
        fetchSubAccounts,
        fetchSubAccountUsers,
        fetchPendingRequests,
        fetchApprovedRequests,
        fetchRejectedRequests,
        refreshAll,

        // Access operations (to be called from UI)
        createAccessBindingFromApi,
        removeAccessBindingFromApi,
        modifyAccessBindingFromApi,
        approvePendingRequestFromApi,
        rejectPendingRequestFromApi,

        // Context state
        initialized
    };

    // Render provider with value
    return (
        <GmcContext.Provider value={contextValue}>
            {children}
        </GmcContext.Provider>
    );
};

// Custom hook to use the GMC context
export const useGmc = () => {
    const context = useContext(GmcContext);
    if (!context) {
        throw new Error('useGmc must be used within a GmcProvider');
    }
    return context;
};

export default GmcContext;
